'use client'

import { Menu } from 'lucide-react'
import { LocaleLink } from '@/components/LocaleLink'
import { useTranslations } from 'next-intl'
import React from 'react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ModoEscuroDropdownItem } from '@/providers/Theme/ModoEscuroToggle'
import {
  LanguageFlagMenu,
  type LanguageFlagMenuProps,
} from '@/modules/languages/components/LanguageFlagMenu.client'

import type {
  ResolvedNavTopButton,
  ResolvedNavTopQuickMenu,
} from '@/Header/utilities/resolveHeaderNavigation'

/** Dados do seletor de idioma (subset do languageChrome) — opcional. */
export type HeaderLanguageData = LanguageFlagMenuProps & { enabled: boolean }

type Props = {
  login: ResolvedNavTopButton
  signup: ResolvedNavTopButton
  quickMenu: ResolvedNavTopQuickMenu
  language?: HeaderLanguageData
}

export function HeaderActions({ login, signup, quickMenu, language }: Props) {
  const t = useTranslations('Header')
  const showLanguage = Boolean(language?.enabled && language.languages.length > 0)
  const hasQuickDropdown =
    quickMenu.search.enabled || quickMenu.archive.enabled || quickMenu.showTheme || showLanguage

  return (
    <nav className="hidden items-center gap-x-2 lg:flex" aria-label={t('accountTools')}>
      {login.enabled ? (
        <Button
          variant="ghost"
          size="clear"
          className="h-9 shrink-0 px-4 font-semibold text-primary hover:text-primary"
          asChild
        >
          <LocaleLink
            href={login.href}
            className="truncate sm:overflow-visible sm:text-clip sm:whitespace-normal"
          >
            {login.label}
          </LocaleLink>
        </Button>
      ) : null}
      {signup.enabled ? (
        <Button size="clear" className="h-9 shrink-0 px-4 font-semibold" asChild>
          <LocaleLink
            href={signup.href}
            className="truncate sm:overflow-visible sm:text-clip sm:whitespace-normal"
          >
            {signup.label}
          </LocaleLink>
        </Button>
      ) : null}
      {hasQuickDropdown ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="clear"
              className="size-9 shrink-0 rounded-[6px] hover:bg-muted"
              aria-label={t('quickMenu')}
            >
              <Menu className="size-[18px]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[10rem]">
            {quickMenu.search.enabled ? (
              <DropdownMenuItem asChild>
                <LocaleLink href={quickMenu.search.href}>{quickMenu.search.label}</LocaleLink>
              </DropdownMenuItem>
            ) : null}
            {quickMenu.archive.enabled ? (
              <DropdownMenuItem asChild>
                <LocaleLink href={quickMenu.archive.href}>{quickMenu.archive.label}</LocaleLink>
              </DropdownMenuItem>
            ) : null}
            {quickMenu.showTheme && (quickMenu.search.enabled || quickMenu.archive.enabled) ? (
              <DropdownMenuSeparator />
            ) : null}
            {quickMenu.showTheme ? <ModoEscuroDropdownItem /> : null}
            {showLanguage && language ? (
              <LanguageFlagMenu
                currentLocale={language.currentLocale}
                defaultLocale={language.defaultLocale}
                languages={language.languages}
                showFlags={language.showFlags}
              />
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </nav>
  )
}
